import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { CalendarClock, Save, Sparkles, Play, Award, DoorOpen, Eye, Loader2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, STATUS_COLORS } from "../lib/bookingStatus";
import { deDateTime } from "../lib/dateFormat";

/**
 * Feb 2026 · Automatische Kunden-Nachrichten · Admin-Editor.
 *
 * Anlässe: Geburtstag · Neujahr · Jahrestag · Willkommen-zurück
 * Live-Preview: rechts vom Editor rendert der Backend `/render`-Endpoint
 * die aktuelle Vorlage mit dem Vornamen (+ ggf. Objekt/Concierge/Jahre)
 * eines echten Kunden — der Admin sieht ohne Umweg wie die E-Mail beim
 * Empfänger aussieht.
 *
 * Sidebar-Route: /team/settings/automated-messages (admin-only).
 */

const KIND_META = {
  birthday:     { title: "Geburtstags-Nachricht", icon: Sparkles,     placeholders: ["Vorname", "Jahr"] },
  new_year:     { title: "Neujahrsgruß",           icon: CalendarClock, placeholders: ["Vorname", "Jahr"] },
  anniversary:  { title: "Jahrestag",              icon: Award,         placeholders: ["Vorname", "Jahr", "Jahre"] },
  welcome_back: { title: "Willkommen zurück",      icon: DoorOpen,      placeholders: ["Vorname", "Jahr", "Objekt", "ConciergeSuffix", "ConciergeNotiz", "ConciergeNotizBlock"] },
};

const KIND_ORDER = ["birthday", "new_year", "anniversary", "welcome_back"];

function PlaceholderHint({ tokens }) {
  return (
    <span className="text-[11.5px] text-navy-dark/60 leading-relaxed">
      Verfügbare Platzhalter:{" "}
      {tokens.map((t, i) => (
        <span key={t}>
          <code className="font-mono bg-ivory px-1 py-0.5 rounded border border-navy/15">{`{{${t}}}`}</code>
          {i < tokens.length - 1 ? " " : ""}
        </span>
      ))}
    </span>
  );
}

function TemplateCard({ kind, value, onChange, active, onFocus }) {
  const { title, icon: Icon, placeholders } = KIND_META[kind];
  return (
    <section
      onClick={() => onFocus?.(kind)}
      className={`bg-white border p-6 space-y-4 cursor-pointer transition-colors ${
        active ? "border-gold ring-1 ring-gold/40" : "border-navy/10 hover:border-navy/30"
      }`}
      data-testid={`tmpl-${kind}-card`}
    >
      <header className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(201,169,97,0.14)", color: BRAND_COLORS.goldDark }}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg text-navy leading-tight">{title}</div>
          <div className="text-[12px] text-navy-dark/60">
            {kind === "welcome_back"
              ? "Wird bei Rückkehr (Abwesenheits-Ende) versendet."
              : "Sendet automatisch um 09:00 Uhr Berlin."}
          </div>
        </div>
        <label
          className="ml-auto flex items-center gap-2 text-[12px] text-navy-dark/70 cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={!!value?.enabled}
            onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
            data-testid={`tmpl-${kind}-enabled`}
            className="w-4 h-4"
          />
          Aktiv
        </label>
      </header>
      <div className="space-y-2">
        <label className="text-[11.5px] tracking-wider uppercase text-navy-dark/60">Betreff</label>
        <input
          type="text"
          value={value?.subject || ""}
          onChange={(e) => onChange({ ...value, subject: e.target.value })}
          onFocus={() => onFocus?.(kind)}
          data-testid={`tmpl-${kind}-subject`}
          className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[14px]"
        />
      </div>
      <div className="space-y-2">
        <label className="text-[11.5px] tracking-wider uppercase text-navy-dark/60">E-Mail-Text (HTML erlaubt)</label>
        <textarea
          rows={9}
          value={value?.body_html || ""}
          onChange={(e) => onChange({ ...value, body_html: e.target.value })}
          onFocus={() => onFocus?.(kind)}
          data-testid={`tmpl-${kind}-body`}
          className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13.5px] font-mono leading-relaxed"
        />
      </div>
      <PlaceholderHint tokens={placeholders} />
    </section>
  );
}

function LivePreviewPanel({ activeKind, settings, customers }) {
  // Feb 2026 · Live-Preview: rechte Spalte, sticky. Der Admin wählt einen
  // Kunden — die Vorlage wird via /render mit dessen Vornamen (+ Objekt/
  // Concierge/Jahre wo relevant) gerendert. Debounced auf Änderungen der
  // aktuellen Vorlage, damit typing sofort spürbar ist.
  const [customerId, setCustomerId] = useState("");
  const [rendered, setRendered] = useState(null);
  const [loading, setLoading] = useState(false);
  const meta = KIND_META[activeKind];
  const currentTpl = settings?.[activeKind];
  const hasTpl = !!currentTpl;
  const tplSubject = currentTpl?.subject;
  const tplBody = currentTpl?.body_html;

  useEffect(() => {
    if (!activeKind || !hasTpl) return;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/team/settings/automated-messages/render", {
          kind: activeKind,
          customer_id: customerId || null,
        });
        setRendered(data);
      } catch (e) {
        toast.error(formatErr(e?.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [activeKind, customerId, hasTpl, tplSubject, tplBody]);

  return (
    <section
      className="bg-navy text-ivory p-6 space-y-4 sticky top-24 self-start"
      data-testid="live-preview-panel"
    >
      <header className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gold text-navy">
          <Eye className="w-5 h-5" strokeWidth={1.7} />
        </div>
        <div>
          <div className="font-display text-lg leading-tight">Live-Vorschau</div>
          <div className="text-[12px] text-ivory/70">
            {meta?.title || "Anlass wählen"} · gerendert für einen Kunden.
          </div>
        </div>
      </header>
      <div className="space-y-1">
        <label className="text-[11px] tracking-wider uppercase text-ivory/60">Kunde</label>
        <select
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          data-testid="live-preview-customer"
          className="w-full bg-navy-dark border border-ivory/25 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-ivory"
        >
          <option value="">— Musterkunde („Anna") —</option>
          {(customers || []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || c.email}
              {c.birthday ? " · 🎂" : ""}
              {c.membership_since ? ` · seit ${c.membership_since.slice(0, 4)}` : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-ivory text-navy p-5 min-h-[280px]" data-testid="live-preview-frame">
        {loading ? (
          <div className="flex items-center gap-2 text-navy-dark/60 text-[13px]">
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
            Rendere …
          </div>
        ) : rendered ? (
          <div>
            <div className="border-b border-navy/15 pb-2 mb-3">
              <div className="text-[10.5px] label-caps text-navy-dark/50 mb-0.5">Betreff</div>
              <div className="font-display text-[15px]" data-testid="live-preview-subject">{rendered.subject}</div>
            </div>
            <div
              className="text-[13.5px] leading-relaxed prose-sm max-w-none"
              data-testid="live-preview-body"
              // Feb 2026 · 27 Feb 2026 · Security-Fix: Body-HTML kommt vom
              // Backend-Render aus Admin-editierbaren Templates. Trotz
              // Admin-Only-Editor sanitizen wir mit DOMPurify — verhindert,
              // dass ein späterer Vorlagen-Import / eine Copy-Paste-Aktion
              // versehentlich Script-Tags in die Live-Preview schleust.
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rendered.body_html || "") }}
            />
            {!rendered.enabled && (
              <div className="mt-4 pt-3 border-t border-navy/10 text-[11.5px] italic" style={{ color: STATUS_COLORS.red }}>
                Diese Vorlage ist deaktiviert — der Cron würde sie überspringen.
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-navy/10 text-[10.5px] text-navy-dark/50">
              Kontext: Vorname „{rendered.context.first_name}"
              {rendered.context.property_name ? ` · Objekt „${rendered.context.property_name}"` : ""}
              {rendered.context.concierge_name ? ` · Concierge „${rendered.context.concierge_name}"` : ""}
              {activeKind === "anniversary" ? ` · Jahre ${rendered.context.years}` : ""}
            </div>
          </div>
        ) : (
          <div className="text-navy-dark/50 italic text-[13px]">Wähle einen Anlass links, um die Vorschau zu starten.</div>
        )}
      </div>
    </section>
  );
}

function DryRunList({ items, emptyLabel, testid, extraLabel }) {
  if (!items || items.length === 0) {
    return <div className="text-[13px] text-navy-dark/60 italic p-3" data-testid={`${testid}-empty`}>{emptyLabel}</div>;
  }
  return (
    <ul className="space-y-1" data-testid={testid}>
      {items.map((it) => (
        <li key={`${testid}-${it.id}`} className="text-[13px] text-navy flex items-center gap-2 flex-wrap">
          <span className="w-1.5 h-1.5 rounded-full bg-gold" />
          <span className="font-medium">{it.first_name}</span>
          <span className="text-navy-dark/50">·</span>
          <span className="text-navy-dark/70">{it.email}</span>
          {extraLabel && <span className="text-navy-dark/50 ml-2">{extraLabel(it)}</span>}
        </li>
      ))}
    </ul>
  );
}

export default function TeamAutomatedMessages() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [saving, setSaving] = useState(false);
  const [activeKind, setActiveKind] = useState("birthday");
  const [customers, setCustomers] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDate, setPreviewDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dryRun, setDryRun] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: cs }] = await Promise.all([
          api.get("/team/settings/automated-messages"),
          api.get("/team/customers").catch(() => ({ data: [] })),
        ]);
        const initial = {};
        for (const k of KIND_ORDER) initial[k] = s[k];
        setSettings(initial);
        setMeta({ updated_at: s.updated_at, updated_by: s.updated_by });
        setCustomers(cs || []);
      } catch (e) {
        toast.error(formatErr(e?.response?.data?.detail));
      }
    })();
  }, []);

  const updateKind = (kind, patch) => {
    setSettings((prev) => ({ ...prev, [kind]: patch }));
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const body = {};
      for (const k of KIND_ORDER) body[k] = settings[k];
      const { data } = await api.put("/team/settings/automated-messages", body);
      const merged = {};
      for (const k of KIND_ORDER) merged[k] = data[k];
      setSettings(merged);
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
      toast.success("Vorlagen gespeichert.");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const runDryRun = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/team/settings/automated-messages/preview", {
        iso_date: previewDate,
      });
      setDryRun(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setPreviewLoading(false);
    }
  };

  const dryRunCount = useMemo(() => {
    if (!dryRun) return 0;
    return (dryRun.birthdays_today?.length || 0)
      + (dryRun.anniversaries_today?.length || 0)
      + (dryRun.welcome_back_today?.length || 0)
      + (dryRun.is_new_year ? (dryRun.new_year_recipients?.length || 0) : 0);
  }, [dryRun]);

  if (user?.role !== "admin") {
    return (
      <div className="p-8" data-testid="automated-messages-forbidden">
        <div className="max-w-2xl bg-white border-l-4 border-red-500 p-6">
          <div className="label-caps text-red-700 mb-1">Kein Zugriff</div>
          <div className="text-navy-dark">Diese Einstellungen sind Admins vorbehalten.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-[1500px] mx-auto space-y-8" data-testid="automated-messages-page">
      <header className="space-y-2">
        <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>Einstellungen · Automatische Nachrichten</div>
        <h1 className="font-display text-3xl text-navy">Geburtstag · Neujahr · Jahrestag · Willkommen zurück</h1>
        <p className="text-[14px] text-navy-dark/70 max-w-3xl leading-relaxed">
          Vier Anlässe, vier Vorlagen. Ein täglicher Cron um <strong>07:55 UTC</strong>
          {" "}(≈ 08:55/09:55 Berlin) versendet die passenden E-Mails automatisch.
          Klicken Sie eine Karte an, um sie in der Live-Vorschau rechts zu sehen —
          mit dem Vornamen eines echten Kunden gerendert.
        </p>
        {meta.updated_at && (
          <div className="text-[11.5px] text-navy-dark/50">
            Zuletzt bearbeitet: {deDateTime(meta.updated_at)}
          </div>
        )}
      </header>

      {settings && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-6 items-start">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {KIND_ORDER.map((k) => (
              <TemplateCard
                key={k}
                kind={k}
                value={settings[k]}
                onChange={(v) => updateKind(k, v)}
                active={activeKind === k}
                onFocus={setActiveKind}
              />
            ))}
          </div>
          <LivePreviewPanel activeKind={activeKind} settings={settings} customers={customers} />
        </div>
      )}

      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving || !settings}
          data-testid="automated-messages-save"
          className="inline-flex items-center gap-2 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-60 px-6 py-2.5 text-[12px] tracking-[0.15em] uppercase"
        >
          <Save className="w-4 h-4" strokeWidth={1.7} />
          {saving ? "Speichern …" : "Vorlagen speichern"}
        </button>
      </div>

      <section className="bg-white border border-navy/10 p-6 space-y-4" data-testid="automated-messages-preview">
        <header className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(15,30,61,0.08)", color: BRAND_COLORS.navy }}>
            <Play className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-display text-lg text-navy">Wer bekommt heute eine E-Mail?</div>
            <div className="text-[12px] text-navy-dark/60">Dry-Run für ein Datum — es werden KEINE Mails versendet.</div>
          </div>
        </header>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-[11.5px] tracking-wider uppercase text-navy-dark/60 block mb-1">Datum</label>
            <input
              type="date"
              value={previewDate}
              onChange={(e) => setPreviewDate(e.target.value)}
              data-testid="automated-messages-preview-date"
              className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px]"
            />
          </div>
          <button
            type="button"
            onClick={runDryRun}
            disabled={previewLoading}
            data-testid="automated-messages-preview-run"
            className="inline-flex items-center gap-2 bg-gold text-navy hover:brightness-95 disabled:opacity-60 px-4 py-2 text-[12px] tracking-[0.15em] uppercase"
          >
            <Play className="w-4 h-4" strokeWidth={2} />
            {previewLoading ? "Prüfe …" : "Dry-Run starten"}
          </button>
          {dryRun && (
            <div className="ml-auto text-[12px] text-navy-dark/70 tabular-nums">
              <strong className="text-navy">{dryRunCount}</strong> E-Mail{dryRunCount === 1 ? "" : "s"} würden versendet.
            </div>
          )}
        </div>
        {dryRun && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-3 border-t border-navy/10">
            <div>
              <div className="label-caps text-[10.5px] text-navy-dark/60 mb-2">Geburtstage am {dryRun.iso_date}</div>
              <DryRunList
                items={dryRun.birthdays_today}
                emptyLabel="Kein Kunde hat an diesem Tag Geburtstag."
                testid="preview-birthdays"
                extraLabel={(it) => `(${it.birthday})`}
              />
            </div>
            <div>
              <div className="label-caps text-[10.5px] text-navy-dark/60 mb-2">Jahrestage am {dryRun.iso_date}</div>
              <DryRunList
                items={dryRun.anniversaries_today}
                emptyLabel="Kein Kunde hat an diesem Tag Jahrestag."
                testid="preview-anniversaries"
                extraLabel={(it) => `(${it.years} Jahr${it.years === 1 ? "" : "e"})`}
              />
            </div>
            <div>
              <div className="label-caps text-[10.5px] text-navy-dark/60 mb-2">Rückkehr am {dryRun.iso_date} (Willkommen zurück)</div>
              <DryRunList
                items={dryRun.welcome_back_today}
                emptyLabel="Keine Kunden-Rückkehr an diesem Tag."
                testid="preview-welcome-back"
                extraLabel={(it) => it.property_name ? `(${it.property_name})` : null}
              />
            </div>
            <div>
              <div className="label-caps text-[10.5px] text-navy-dark/60 mb-2">
                Neujahrsgruß {dryRun.is_new_year ? `an ${dryRun.new_year_recipients?.length || 0} Kunden` : "(nicht heute)"}
              </div>
              {dryRun.is_new_year ? (
                <DryRunList
                  items={dryRun.new_year_recipients}
                  emptyLabel="Keine aktiven Kunden im System."
                  testid="preview-new-year"
                />
              ) : (
                <div className="text-[13px] text-navy-dark/60 italic p-3">Der Neujahrsgruß wird ausschließlich am 1. Januar versendet.</div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
